import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { FileText, Clock, CheckCircle, DollarSign } from 'lucide-react'
import type { AdminStats } from '../types'

interface PricingStatsCardsProps {
  stats?: AdminStats
  isLoading?: boolean
}

interface StatCardProps {
  titulo: string
  valor: string | number
  icon: React.ReactNode
  extra?: React.ReactNode
}

function StatCard({ titulo, valor, icon, extra }: StatCardProps) {
  return (
    <Card className="p-6 border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-teal-500/15 via-teal-500/5 via-50% to-transparent dark:from-teal-800/20 dark:via-teal-900/10 dark:via-50% dark:to-transparent">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium text-muted-foreground">{titulo}</p>
          <p className="text-3xl font-bold text-black dark:text-white">{valor}</p>
          {extra}
        </div>
        {icon}
      </div>
    </Card>
  )
}

function StatCardSkeleton() {
  return (
    <Card className="p-6">
      <div className="flex items-start justify-between">
        <div className="space-y-2 flex-1">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-16" />
        </div>
        <Skeleton className="h-10 w-10 rounded-lg" />
      </div>
    </Card>
  )
}

export function PricingStatsCards({ stats, isLoading }: PricingStatsCardsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <StatCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  if (!stats) return null

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <StatCard
        titulo="Total Cotizaciones"
        valor={stats.total_cotizaciones}
        icon={<FileText className="h-8 w-8 text-teal-500" />}
      />
      <StatCard
        titulo="Pendientes"
        valor={stats.pendientes}
        icon={<Clock className="h-8 w-8 text-teal-500" />}
      />
      <StatCard
        titulo="Atendidas"
        valor={stats.atendidas}
        icon={<CheckCircle className="h-8 w-8 text-teal-500" />}
        extra={
          <Badge className="badge-gradient hover:opacity-90 mt-1">
            {stats.tasa_conversion}% conversión
          </Badge>
        }
      />
      <StatCard
        titulo="Valor Total"
        valor={`$${stats.valor_total.toLocaleString('es-EC', { minimumFractionDigits: 2 })}`}
        icon={<DollarSign className="h-8 w-8 text-teal-500" />}
      />
    </div>
  )
}
